import axios from 'axios';

export default class PagesService
{
    public static async getFolders(website: number, locale: string): Promise<Pagination<Resource<FolderListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<FolderListItemModel>>>('admin/cms/pages/folders', {
            params: {
                website: website,
                locale: locale
            }
        })).data;
    }

    public static async fetchFolder(id: number): Promise<Resource<FolderFormModel>>
    {
        return (await axios.get<Resource<FolderFormModel>>(`admin/cms/pages/folders/${id}`)).data;
    }

    public static async createFolder(model: FolderFormModel): Promise<Resource<FolderFormModel>>
    {
        return (await axios.post<Resource<FolderFormModel>>(`admin/cms/pages/folders`, model)).data;
    }

    public static async updateFolder(id: number, model: FolderFormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/cms/pages/folders/${id}`, model)).data;
    }

    public static async removeFolder(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/cms/pages/folders/${id}`)).data as Statement;
    }

    // ----------------------------------------------------------------------------------------------------

    public static async getPages(folders: number[], expanded: number[]): Promise<Pagination<Resource<PageListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<PageListItemModel>>>('admin/cms/pages', {
            params: {
                folders: folders.join(','),
                expanded: expanded.join(',')
            }
        })).data;
    }

    public static async getPagesByFolder(folderId: number): Promise<PageListItemModel[]>
    {
        return (await axios.get<PageListItemModel[]>('admin/cms/pages/options', {
            params: {
                folder: folderId
            }
        })).data;
    }

    public static async getPagesByModule(websiteId: number, locale: string, moduleName: string): Promise<PageListItemModel[]>
    {
        return (await axios.get<PageListItemModel[]>('admin/cms/pages/modules', {
            params: {
                website: websiteId,
                locale: locale,
                module: moduleName
            }
        })).data;
    }

    public static async getPagesPublicationsByIds(pagesIds: number[]): Promise<PagePublication[]>
    {
        return (await axios.get<PagePublication[]>('admin/cms/pages/publications', {
            params: {
                pagesIds: pagesIds
            }
        })).data;
    }

    public static async fetchPage(id: number): Promise<Resource<PageFormModel>>
    {
        return (await axios.get<Resource<PageFormModel>>(`admin/cms/pages/${id}`)).data;
    }

    public static async createPage(model: PageFormModel): Promise<Resource<PageFormModel>>
    {
        return (await axios.post<Resource<PageFormModel>>(`admin/cms/pages`, model)).data;
    }

    public static async updatePage(id: number, model: PageFormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/cms/pages/${id}`, model)).data;
    }

    public static async removePage(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/cms/pages/${id}`)).data as Statement;
    }

    public static async sortPages(positions: PagePosition[]): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/cms/pages/positions`, positions)).data;
    }
}

export interface FolderListItemModel
{
    id: number;
    name: string;
    locale: string;
}

export interface FolderFormModel
{
    id: number;
    name: string;
    websiteId: number;
    locale: string;
}

export interface PageListItemModel
{
    id: number;
    parentId?: number;
    folderId: number;
    name: string;
    option: string;
    module: string;
    pageMode: PageModeEnum;
    mainLayoutId: number | null;
    altLayoutId: number | null;
    level: number;
    hasChildren: boolean;
    position: number;
    hasCustomLayout: boolean;
    disabled?: boolean;
    isHidden: boolean;
}

export interface PageFormModel
{
    id: number;
    folderId: number;
    parentId?: number;
    name: string;
    symbol: string;
    isActive: boolean;
    isHidden: boolean;
    authOnly: boolean | null;
    requireAccess: boolean;
    isSluggable: boolean;
    slug: string;
    pageMode: string;
    mainLayoutId: number | null;
    altLayoutId: number | null;
    isLayoutInherited: boolean;
    module: string;
    aliasId?: number;
    linkUrl: string;
    linkTarget: boolean;
    linkParams: string;
    settings: any;
    meta: MetaSettingsModel | null;
}

export interface PagePosition
{
    id: number;
    position: number;
}
